import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useTenant } from "@/lib/tenant";
import { PageHeader, Panel, SevBadge, Empty } from "@/components/Bits";
import { toast } from "sonner";
import { Mail, ShieldAlert } from "lucide-react";

const AuthCheck = ({ label, val }) => {
  const ok = val === "pass";
  return <span className={`font-mono text-[10px] px-1.5 py-0.5 border ${ok ? "border-emerald-800 text-emerald-400" : "border-red-900 text-red-400"}`}>{label}:{val}</span>;
};

export default function EmailSecurity() {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const { data = [] } = useQuery({
    queryKey: ["emails", tenantId],
    queryFn: () => api.get(`/emails/${tenantId}`).then((r) => r.data),
    enabled: !!tenantId,
  });

  const quar = useMutation({
    mutationFn: (id) => api.post(`/email/${id}/quarantine`).then((r) => r.data),
    onSuccess: () => { toast.success("Message quarantined"); qc.invalidateQueries({ queryKey: ["emails", tenantId] }); },
  });

  if (!tenantId) return <Empty msg="Select an MSP tenant" />;

  return (
    <>
      <PageHeader title="Email Security" kicker="M365 · Gmail · IMAP connectors" />
      <div className="p-6 grid grid-cols-1 xl:grid-cols-2 gap-4">
        {data.map((m) => (
          <Panel key={m.id} className="panel-hover" kicker={`Received · ${new Date(m.received_at).toLocaleString()}`}>
            <div className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{m.subject}</div>
                  <div className="text-xs text-zinc-400 truncate font-mono">{m.sender} → {m.recipient}</div>
                </div>
                <SevBadge sev={m.risk} />
              </div>
              <div className="flex gap-2 flex-wrap">
                <AuthCheck label="SPF" val={m.spf} />
                <AuthCheck label="DKIM" val={m.dkim} />
                <AuthCheck label="DMARC" val={m.dmarc} />
              </div>
              <ul className="text-xs text-zinc-300 space-y-1 list-disc list-inside">
                {m.reasons.map((r) => <li key={r}>{r}</li>)}
              </ul>
              <div className="flex items-center justify-between pt-2 border-t hairline">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  {m.quarantined ? "QUARANTINED" : "IN INBOX"}
                </span>
                {!m.quarantined && (
                  <button data-testid={`quarantine-${m.id}`} onClick={() => quar.mutate(m.id)}
                    className="border border-red-900 bg-red-950/40 text-red-400 hover:bg-red-950/70 px-3 py-1 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1 transition-colors duration-150">
                    <ShieldAlert size={11} /> QUARANTINE
                  </button>
                )}
              </div>
            </div>
          </Panel>
        ))}
        {data.length === 0 && <Empty msg="No email messages scanned yet" />}
      </div>
    </>
  );
}
